import { Camera, ImagePlus, Pencil, Plus, Printer, SquareCheck, Trash2, Watch as WatchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { dateTime } from "../../format";
import type { TicketDetail, Watch } from "../../types";
import { Card } from "./Card";
import { CameraDialog } from "./Camera";
import { CheckInWizard } from "./CheckInWizard";
import { useTicketContext } from "./useTicket";

type Photo = TicketDetail["photos"][number];
/** What the camera window is for: a new watch (AI fills it in), another angle of a watch, or a ticket photo. */
type Shot = { kind: "newWatch" } | { kind: "extra"; watchId: string; label: string } | { kind: "ticket" };

/**
 * Watches (owner, 2026-11-03): one card for check-in and the watches' photos and details.
 * Before check-in: "Start check-in" opens the guided window. After: each watch stays editable, can get more
 * photos, and more watches can be added; the check-in notes and slips are here too.
 */
export function WatchesCard() {
  const { detail, save, saving } = useTicketContext();
  const t = detail.ticket;
  const [wizard, setWizard] = useState(false);
  const [shot, setShot] = useState<Shot | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [desc, setDesc] = useState(t.description ?? "");
  useEffect(() => setDesc(t.description ?? ""), [t.description]);
  const printSlips = () => window.open(`/print/checkin/${t.ticketNo}`, "_blank");
  const loose = detail.photos.filter((p) => !p.watchId || !detail.watches.some((w) => w.id === p.watchId));
  const checkedInBy = t.checkedInAt ? whoCheckedIn(detail, t.checkedInAt) : null;

  const takePhoto = async (data: string) => {
    if (!shot) return;
    const body = shot.kind === "newWatch" ? { identify: true, newWatch: true } : shot.kind === "extra" ? { extraFor: shot.watchId } : {};
    const d = await save("POST", "/photos", { data, contentType: "image/jpeg", ...body });
    setStatus(d ? (shot.kind === "newWatch" ? d.notice ?? "Saved." : "Saved. Take another or press Done.") : null);
    if (d && shot.kind === "newWatch") setShot(null); // the new watch opens for editing below
  };

  return (
    <Card title="Watches" icon={WatchIcon}
      actions={t.checkedInAt ? <button type="button" className="icon-link" onClick={printSlips} title="Reprint the check-in slip and work slips (4×6)" aria-label="Reprint check-in slips"><Printer size={18} /></button> : undefined}>
      {detail.watches.length > 0 && (
        <ul className="watch-list">
          {detail.watches.map((w) => (
            <WatchItem key={w.id} w={w} ticketNo={t.ticketNo} photos={detail.photos.filter((p) => p.watchId === w.id || p.id === w.photoId)}
              onAddPhoto={() => { setStatus(null); setShot({ kind: "extra", watchId: w.id, label: `${t.ticketNo}-${w.position}` }); }} />
          ))}
        </ul>
      )}

      {!t.checkedInAt ? (
        <div className="checkin-start">
          {!detail.watches.length && <p className="hint">No watches yet. Check-in starts with a photo of the first watch; the AI fills in its details.</p>}
          <button type="button" className="btn primary block" disabled={saving} onClick={() => setWizard(true)}>
            <SquareCheck size={18} />{detail.watches.length ? "Continue check-in" : "Start check-in"}</button>
        </div>
      ) : (
        <div className="btn-row wrap">
          <button type="button" className="btn outline" disabled={saving} onClick={() => { setStatus(null); setShot({ kind: "newWatch" }); }}>
            <Camera size={16} />Add watch</button>
          <button type="button" className="link-btn" disabled={saving} onClick={() => void save("POST", "/watches", {})}><Plus size={15} />Add without photo</button>
        </div>
      )}

      {loose.length > 0 && (
        <div className="sub-section">
          <div className="sub-head"><span className="lbl">Other photos</span></div>
          <PhotoStrip photos={loose} />
        </div>
      )}

      <label className="field-lbl">Check-in notes
        <textarea rows={3} placeholder="What the customer reported, condition notes, special instructions…" value={desc} onChange={(e) => setDesc(e.target.value)} /></label>
      <div className="btn-row between">
        <button type="button" className="link-btn" disabled={saving} onClick={() => { setStatus(null); setShot({ kind: "ticket" }); }}><ImagePlus size={15} />Add ticket photo</button>
        <button type="button" className="btn outline" disabled={desc === (t.description ?? "") || saving}
          onClick={() => void save("PATCH", "", { description: desc, expectedUpdatedAt: t.updatedAt })}>Update notes</button>
      </div>
      {t.checkedInAt && (
        <div className="checked-in">
          <span>Checked in {dateTime(t.checkedInAt)}{checkedInBy ? ` by ${checkedInBy}` : ""}</span>
          <button type="button" className="link-btn" onClick={printSlips}><Printer size={15} />Reprint slips</button>
        </div>
      )}

      {shot && (
        <CameraDialog busy={saving} status={status} onClose={() => setShot(null)}
          title={shot.kind === "newWatch" ? "New watch (AI identifies it)" : shot.kind === "extra" ? `More photos of watch ${shot.label}` : "Ticket photo"}
          hint={shot.kind === "newWatch" ? "Dial facing the camera. The AI fills in brand, model and description for you to check." : undefined}
          onPhoto={takePhoto} />
      )}
      {wizard && <CheckInWizard onClose={() => setWizard(false)} />}
    </Card>
  );
}

/** Who moved the ticket to Pending Inspection at check-in, from the status history. */
function whoCheckedIn(detail: TicketDetail, at: string): string | null {
  const when = new Date(at).getTime();
  const h = detail.history.find((x) => x.kind === "status" && x.to === "Pending Inspection" && Math.abs(new Date(x.at).getTime() - when) < 120_000);
  return h?.by || null;
}

/** Photos in a row, each opens full size and can be deleted. */
function PhotoStrip({ photos, mainId }: { photos: Photo[]; mainId?: string | null }) {
  const { save, saving } = useTicketContext();
  return (
    <ul className="photo-strip">
      {photos.map((p) => (
        <li key={p.id} className={p.id === mainId ? "main" : ""}>
          {p.url ? <a href={p.url} target="_blank" rel="noreferrer" title="Open full size"><img src={p.url} alt="" loading="lazy" /></a>
            : <span className="hint">{p.originalName ?? "photo"}</span>}
          <button type="button" className="photo-del" aria-label="Delete photo" title="Delete photo" disabled={saving}
            onClick={() => { if (window.confirm(p.legacy ? "Remove this old-system photo from the portal?" : "Delete this photo permanently? It can't be undone.")) void save("DELETE", `/photos/${p.id}`); }}>
            <Trash2 size={13} /></button>
        </li>
      ))}
    </ul>
  );
}

/** One watch: number, brand and model, description and condition, its photos; Edit opens the fields. */
function WatchItem({ w, ticketNo, photos, onAddPhoto }: { w: Watch; ticketNo: number; photos: Photo[]; onAddPhoto: () => void }) {
  const { save, saving, lookups } = useTicketContext();
  const blank = !w.brand && !w.model && !w.description;
  const [edit, setEdit] = useState(blank);
  const fields = () => ({ brand: w.brand ?? "", model: w.model ?? "", description: w.description ?? "", condition: w.condition ?? "" });
  const [f, setF] = useState(fields);
  useEffect(() => setF(fields()), [w]); // eslint-disable-line react-hooks/exhaustive-deps
  const label = `${ticketNo}-${w.position}`;
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  const ordered = [...photos].sort((a, b) => (a.id === w.photoId ? -1 : b.id === w.photoId ? 1 : a.createdAt.localeCompare(b.createdAt)));
  return (
    <li className="watch-item">
      <div className="watch-head">
        <span className="watch-no">{label}</span>
        <strong className="watch-name">{[w.brand, w.model].filter(Boolean).join(" ") || "Watch"}</strong>
        {!edit && <button type="button" className="icon-link" aria-label={`Edit watch ${label}`} title="Edit details" onClick={() => setEdit(true)}><Pencil size={15} /></button>}
        <button type="button" className="icon-link" aria-label={`Add a photo of watch ${label}`} title="Add a photo of this watch" onClick={onAddPhoto}><Camera size={15} /></button>
      </div>
      {edit ? (
        <form className="watch-form" onSubmit={async (e) => { e.preventDefault(); if (await save("PUT", `/watches/${w.id}`, f)) setEdit(false); }}>
          <input list="watch-brands" placeholder="Brand" value={f.brand} onChange={set("brand")} aria-label="Brand" autoFocus />
          <input placeholder="Model" value={f.model} onChange={set("model")} aria-label="Model" />
          <input className="wide" placeholder="Description (shown to the customer)" value={f.description} onChange={set("description")} aria-label="Description" />
          <input className="wide" placeholder="Condition (scratches, cracked crystal…)" value={f.condition} onChange={set("condition")} aria-label="Condition" />
          <datalist id="watch-brands">{lookups?.watchBrands.map((b) => <option key={b} value={b} />)}</datalist>
          <div className="btn-row tight">
            <button type="submit" className="btn primary" disabled={saving}>Save</button>
            {!blank && <button type="button" className="btn outline" onClick={() => { setF(fields()); setEdit(false); }}>Cancel</button>}
            <button type="button" className="icon-link danger push" aria-label={`Remove watch ${label}`} title="Remove this watch" disabled={saving}
              onClick={() => { if (window.confirm(`Remove watch ${label}? Its services stay on the estimate, no longer tied to a watch. Its photos stay on the ticket.`)) void save("DELETE", `/watches/${w.id}`); }}><Trash2 size={16} /></button>
          </div>
        </form>
      ) : (
        (w.description || w.condition) && (
          <div className="watch-desc">{w.description}{w.condition && <span className="hint">{w.description ? " · " : ""}Condition: {w.condition}</span>}</div>
        )
      )}
      {ordered.length > 0 && <PhotoStrip photos={ordered} mainId={w.photoId} />}
    </li>
  );
}
